import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import './index.css';

import SharedNavbar from './components/layout/SharedNavbar';
import Footer from './components/layout/Footer';

import Home          from './pages/Home';
import About         from './pages/About';
import Services      from './pages/Services';
import ServiceDetail from './pages/ServiceDetail';
import Projects      from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import Contact       from './pages/Contact';
import BookService   from './pages/BookService';
import VerifyCertificate from './pages/VerifyCertificate';

import AdminLogin        from './pages/admin/AdminLogin';
import AdminDashboard    from './pages/admin/AdminDashboard';
import AdminProjects     from './pages/admin/AdminProjects';
import AdminTeam         from './pages/admin/AdminTeam';
import AdminTestimonials from './pages/admin/AdminTestimonials';
import AdminBookings     from './pages/admin/AdminBookings';
import AdminCertificates from './pages/admin/AdminCertificates';
import AdminSettings     from './pages/admin/AdminSettings';
import AdminLetterhead   from './pages/admin/AdminLetterhead';

/* ── WhatsApp global button ── */
const WhatsAppGlobal = () => (
  <button
    className="wa-global"
    onClick={() => window.open('https://wa.me/233XXXXXXXXX?text=Hello%20Smart%20Quality%20Systems', '_blank', 'noopener')}
    style={{
      position: 'fixed', bottom: '2rem', right: '2rem', zIndex: 9998,
      width: 56, height: 56, borderRadius: '50%',
      background: '#25D366', border: 'none', cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: '0 4px 20px rgba(37,211,102,0.45)',
      animation: 'waPulse 2.5s infinite',
    }}
    aria-label="Chat on WhatsApp">
    <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
    </svg>
    <style>{`
      @media(max-width:480px){ .wa-global { display:none!important; } }
    `}</style>
  </button>
);

/* ── Public layout — navbar + content + footer + WhatsApp ── */
const PublicLayout = ({ children }) => (
  <div style={{ background: 'var(--ivory)', minHeight: '100vh', fontFamily: 'var(--font-sans)' }}>
    <SharedNavbar />
    <main>{children}</main>
    <Footer />
    <WhatsAppGlobal />
  </div>
);

/* ── Home layout — Home has its own navbar, but needs Footer + WA ── */
const HomeLayout = () => (
  <div style={{ background: 'var(--ivory)', minHeight: '100vh', fontFamily: 'var(--font-sans)' }}>
    {/* Home.js renders its own internal navbar */}
    <Home />
    <Footer />
    <WhatsAppGlobal />
  </div>
);

/* ── Protected admin route ── */
const ProtectedRoute = ({ children }) => {
  const { admin, loading } = useAuth();
  if (loading) return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--ivory)' }}>
      <div style={{ width: 40, height: 40, border: '3px solid var(--border)', borderTopColor: 'var(--navy)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
  return admin ? children : <Navigate to="/admin/login" replace />;
};

export default function App() {
  return (
    <AuthProvider>
      <Router>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              fontFamily: 'var(--font-sans)',
              borderRadius: 'var(--radius-md)',
              fontSize: '0.9rem',
              background: 'var(--white)',
              color: 'var(--navy)',
              border: '1px solid var(--border)',
              boxShadow: 'var(--shadow-md)',
            },
          }}
        />
        <Routes>
          {/* ── Public ── */}
          <Route path="/"             element={<HomeLayout />} />
          <Route path="/about"        element={<PublicLayout><About /></PublicLayout>} />
          <Route path="/services"     element={<PublicLayout><Services /></PublicLayout>} />
          <Route path="/services/:id" element={<PublicLayout><ServiceDetail /></PublicLayout>} />
          <Route path="/projects"     element={<PublicLayout><Projects /></PublicLayout>} />
          <Route path="/projects/:id" element={<PublicLayout><ProjectDetail /></PublicLayout>} />
          <Route path="/contact"      element={<PublicLayout><Contact /></PublicLayout>} />
          <Route path="/book-service" element={<PublicLayout><BookService /></PublicLayout>} />
          <Route path="/verify/:serial" element={<PublicLayout><VerifyCertificate /></PublicLayout>} />

          {/* ── Admin ── */}
          <Route path="/admin/login"        element={<AdminLogin />} />
          <Route path="/admin"              element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/projects"     element={<ProtectedRoute><AdminProjects /></ProtectedRoute>} />
          <Route path="/admin/team"         element={<ProtectedRoute><AdminTeam /></ProtectedRoute>} />
          <Route path="/admin/testimonials" element={<ProtectedRoute><AdminTestimonials /></ProtectedRoute>} />
          <Route path="/admin/bookings"     element={<ProtectedRoute><AdminBookings /></ProtectedRoute>} />
          <Route path="/admin/certificates" element={<ProtectedRoute><AdminCertificates /></ProtectedRoute>} />
          <Route path="/admin/settings"     element={<ProtectedRoute><AdminSettings /></ProtectedRoute>} />
          <Route path="/admin/letterhead"   element={<ProtectedRoute><AdminLetterhead /></ProtectedRoute>} />
          <Route path="*"                   element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}